import { useState, useEffect } from "react";
import "./Userroles.css";
import { useNavigate } from "react-router-dom";
import dataService from "../services/dataService";
import authService from "../services/authService";
import { useToast } from "../context/ToastContext";
import Modal from "../components/Modal";

function UserRolesScreen() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { addToast } = useToast();

  const [customers, setCustomers] = useState([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [activeUser, setActiveUser] = useState(null);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await dataService.getUsers();
      setUsers(response.data.data);
    } catch (err) {
      console.error("Failed to fetch users", err);
      setError("Failed to load users. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await dataService.getCustomers();
      setCustomers(response.data.data);
    } catch (err) {
      console.error("Failed to fetch customers", err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
  }, []);

  const handleOpenAssignModal = (user) => {
    setActiveUser(user);
    setSelectedCustomerIds(user.assignedCustomers?.map(c => c._id || c) || []);
    setCustomerSearchTerm("");
    setIsAssignModalOpen(true);
  };

  const handleToggleCustomer = (customerId) => {
    if (selectedCustomerIds.includes(customerId)) {
      setSelectedCustomerIds(selectedCustomerIds.filter(id => id !== customerId));
    } else {
      setSelectedCustomerIds([...selectedCustomerIds, customerId]);
    }
  };

  const handleSelectAllCustomers = () => {
    const filtered = customers.filter(c => {
      const matchesSearch = (c.name || "").toLowerCase().includes(customerSearchTerm.toLowerCase()) || 
                            (c.companyName || "").toLowerCase().includes(customerSearchTerm.toLowerCase());
      if (!matchesSearch) return false;
      
      const isAssignedToOther = users.some(u => 
        u._id !== activeUser?._id && 
        (u.role === 'Staff' || u.role === 'user') &&
        u.assignedCustomers?.some(ac => (ac._id || ac) === c._id)
      );
      return !isAssignedToOther;
    });
    
    const filteredIds = filtered.map(c => c._id);
    const newSelected = Array.from(new Set([...selectedCustomerIds, ...filteredIds]));
    setSelectedCustomerIds(newSelected);
  };

  const handleDeselectAllCustomers = () => {
    const filtered = customers.filter(c => 
      c.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) || 
      (c.companyName || "").toLowerCase().includes(customerSearchTerm.toLowerCase())
    );
    const filteredIds = filtered.map(c => c._id);
    setSelectedCustomerIds(selectedCustomerIds.filter(id => !filteredIds.includes(id)));
  };

  const handleSaveAssignments = async () => {
    try {
      const res = await dataService.updateUser(activeUser._id, {
        assignedCustomers: selectedCustomerIds
      });
      setUsers(users.map(u => u._id === activeUser._id ? res.data.data : u));
      addToast("Customers assigned successfully", "success");
      setIsAssignModalOpen(false);
    } catch (err) {
      console.error("Failed to assign customers", err);
      addToast(err.response?.data?.message || "Failed to assign customers", "error");
    }
  };

  const handleAddUser = () => {
    navigate("/users/add");
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      try {
        await dataService.deleteUser(id);
        setUsers(users.filter(user => user._id !== id));
        addToast("User deleted successfully", "success");
      } catch (err) {
        console.error("Failed to delete user", err);
        addToast(err.response?.data?.message || "Failed to delete user", "error");
      }
    }
  };

  return (
    <div className="user-roles-container">



      <div className="report-content">
        <div className="headings">
          <div className="user-header">
            <div className="headings-text">
              <h1>User Roles & Settings</h1>
              <p>Manage User Access and Permissions</p>
            </div>

            {authService.hasPermission('administration', 'manageUser') && (
              <button className="add-customer-btn"
                onClick={handleAddUser}>
                + Add User
              </button>
            )}
          </div>

          <div className="user-cards">
            <div className="user-card total-users">
              <h4>Total Users</h4>
              <h2>{users.length}</h2>
            </div>

            <div className="user-card active-users">
              <h4>Active Users</h4>
              <h2>{users.filter(u => u.status === 'Active').length}</h2>
            </div>

            <div className="user-card roles">
              <h4>Roles</h4>
              <h2>{new Set(users.map(u => u.role)).size}</h2>
            </div>
          </div>


          {/* User Management Table */}
          <div className="table-card">
            <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>User Management</h3>
              <div className="search-box" style={{ maxWidth: '300px' }}>
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {error && <p className="error-message" style={{ color: 'red', padding: '10px' }}>{error}</p>}

            <table className="user-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Assigned Customers</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th>Active</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>Loading users...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>No users found.</td></tr>
                ) : (
                  users
                    .filter(u =>
                      (u.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (u.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (u.role || "").toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((user) => (
                      <tr key={user._id}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.role}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px', fontWeight: '500' }}>
                              {user.assignedCustomers && user.assignedCustomers.length > 0 ? (
                                user.assignedCustomers.map(c => c.name).join(', ')
                              ) : (
                                <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>None</span>
                              )}
                            </span>
                            {authService.hasPermission('administration', 'manageUser') && (user.role === 'Staff' || user.role === 'user') && (
                              <button
                                className="assign-btn"
                                onClick={() => handleOpenAssignModal(user)}
                                style={{
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  backgroundColor: '#4F46E5',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  transition: 'all 0.2s',
                                  boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#4338CA'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#4F46E5'}
                              >
                                Assign
                              </button>
                            )}
                          </div>
                        </td>
                        <td>{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}</td>
                        <td><span className={`status ${(user.status || "").toLowerCase()}`}>{user.status}</span></td>
                        <td>
                          <input type="checkbox" checked={user.status === 'Active'} readOnly />
                        </td>
                        <td>
                          {authService.hasPermission('administration', 'manageUser') && user.email !== authService.getCurrentUser()?.email && (
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteUser(user._id)}
                              title="Delete User"
                            >
                              🗑️
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          {/* Role Permissions Table */}
          <div className="table-card">
            <div className="table-header">
              <h3>Role Permissions</h3>
            </div>

            <table className="permissions-table">
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Manager</th>
                  <th>Staff</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>View Dashboard</td>
                  <td>View Dashboard</td>
                  <td>View Dashboard</td>
                </tr>

                <tr>
                  <td>Manage Customers</td>
                  <td>Manage Customers</td>
                  <td>View Customers</td>
                </tr>

                <tr>
                  <td>Manage Vendors</td>
                  <td>—</td>
                  <td>—</td>
                </tr>

                <tr>
                  <td>Manage Products</td>
                  <td>Manage Products</td>
                  <td>View Products</td>
                </tr>

                <tr>
                  <td>Create Invoices</td>
                  <td>Create Invoices</td>
                  <td>Create Invoices</td>
                </tr>

                <tr>
                  <td>Manage Payments</td>
                  <td>Manage Payments</td>
                  <td>—</td>
                </tr>

                <tr>
                  <td>View Reports</td>
                  <td>View Reports</td>
                  <td>—</td>
                </tr>

                <tr>
                  <td>System Settings</td>
                  <td>—</td>
                  <td>—</td>
                </tr>
              </tbody>
            </table>
          </div>


        </div>
      </div>

      {/* Assign Customers Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        title={`Assign Customers to ${activeUser?.name}`}
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsAssignModalOpen(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #D1D5DB', cursor: 'pointer', background: '#fff' }}>
              Cancel
            </button>
            <button className="primary-btn" onClick={handleSaveAssignments} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', backgroundColor: '#4F46E5', color: '#fff', fontWeight: '600' }}>
              Save Assignments
            </button>
          </>
        }
      >
        <div className="assign-customers-modal" style={{ minWidth: '450px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', color: '#4B5563', fontWeight: '500' }}>
              Selected: <strong style={{ color: '#4F46E5' }}>{selectedCustomerIds.length}</strong> customer(s)
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                type="button" 
                onClick={handleSelectAllCustomers}
                style={{ fontSize: '12px', padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: '6px', cursor: 'pointer', background: '#F9FAFB', fontWeight: '500' }}
              >
                Select All
              </button>
              <button 
                type="button" 
                onClick={handleDeselectAllCustomers}
                style={{ fontSize: '12px', padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: '6px', cursor: 'pointer', background: '#F9FAFB', fontWeight: '500' }}
              >
                Clear All
              </button>
            </div>
          </div>

          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search customers to assign..."
              value={customerSearchTerm}
              onChange={(e) => setCustomerSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div 
            className="customer-selection-list" 
            style={{ 
              maxHeight: '280px', 
              overflowY: 'auto', 
              border: '1px solid #E5E7EB', 
              borderRadius: '8px',
              padding: '8px'
            }}
          >
            {customers
              .filter(c => 
                (c.name || "").toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
                (c.companyName || "").toLowerCase().includes(customerSearchTerm.toLowerCase())
              )
              .map(customer => {
                const assignedUser = users.find(u => 
                  u._id !== activeUser?._id && 
                  (u.role === 'Staff' || u.role === 'user') &&
                  u.assignedCustomers?.some(c => (c._id || c) === customer._id)
                );
                const isChecked = selectedCustomerIds.includes(customer._id);
                return (
                  <label 
                    key={customer._id} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      padding: '10px 12px', 
                      borderRadius: '6px', 
                      cursor: assignedUser ? 'not-allowed' : 'pointer',
                      background: isChecked ? '#EEF2FF' : 'transparent',
                      opacity: assignedUser ? 0.6 : 1,
                      transition: 'all 0.2s',
                      marginBottom: '4px',
                      borderBottom: '1px solid #F3F4F6'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={!!assignedUser}
                      onChange={() => handleToggleCustomer(customer._id)}
                      style={{ width: '16px', height: '16px', cursor: assignedUser ? 'not-allowed' : 'pointer' }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>{customer.name}</span>
                      {customer.companyName && (
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>{customer.companyName}</span>
                      )}
                    </div>
                    {assignedUser && (
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#DC2626', 
                        backgroundColor: '#FEE2E2', 
                        padding: '3px 8px', 
                        borderRadius: '12px', 
                        fontWeight: '600' 
                      }}>
                        Assigned to {assignedUser.name}
                      </span>
                    )}
                  </label>
                );
              })}
            {customers.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: '14px' }}>
                No customers found.
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
export default UserRolesScreen;